import React, { useEffect, useMemo } from 'react'; // Import React and hooks
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChartPieIcon, XMarkIcon
} from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/24/solid'; // Import Solid Lock icon
import logo from '../../assets/images/logo_50x50.png';
import { useUserRoles } from '../../hooks/useUserRoles'; // Import the hook for admin detection
import { ROLES } from '../../config/routeConfig'; // Import ROLES for admin detection only
import { getSortedRoutes, buildRoutesFromFeatureFlags } from '../../config/featureFlagRouteConfig'; // NEW: Feature flag routing
import { useFeatureFlags } from '../../hooks/useFeatureFlags'; // NEW: Feature flag data
import { useLayoutAnimation } from '../../utils/layoutAnimationManager'; // Import animation manager

interface SidebarProps {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
}

// NOTE: Icons are now defined in featureFlagRouteConfig.ts and accessed via route.icon


const Sidebar: React.FC<SidebarProps> = ({ isSidebarOpen, toggleSidebar }) => {
  // Get roles array for admin detection only (legacy)
  const { roles: userRoles, isLoading: rolesLoading, hasEverLoaded } = useUserRoles();
  
  // NEW: Fetch all feature flags for user
  const { data: featureFlags, isLoading: flagsLoading } = useFeatureFlags();
  
  // Debug log for feature flags loading state
  useEffect(() => {
    if (flagsLoading) {
      console.log('[Sidebar] ⏳ Feature flags are loading - showing skeleton loaders for navigation...');
    } else if (featureFlags) {
      console.log('[Sidebar] ✅ Feature flags loaded - navigation ready with', Object.keys(featureFlags).length, 'flags');
    }
  }, [flagsLoading, featureFlags]);
  
  // Use layout animation manager for consistent state across components
  const { hasCompleted: hasAnimated, markComplete } = useLayoutAnimation();
  
  // Use sessionStorage to persist admin status determination across component remounts
  //const ADMIN_STATUS_KEY = 'admin-status-determined';
  
  // Mark animations complete after initial animation
  useEffect(() => {
    if (!hasAnimated) {
      const timer = setTimeout(() => markComplete(), 1500);
      return () => clearTimeout(timer);
    }
  }, [hasAnimated, markComplete]);
  
  // Compute admin visibility with maximum stability - avoid any flashing
  const isAdminVisible = useMemo(() => {
    const ADMIN_STATUS_KEY = 'admin-status-determined';
    const ADMIN_VALUE_KEY = 'admin-visibility-value';
    const isAdminImpersonating = localStorage.getItem('USER_IS_ADMIN_IMPERSONATING_KEY') === 'true';
    const hasRolesData = userRoles.length > 0;
    const hasAdminRole = userRoles.includes(ROLES.ADMIN);
    const hasEverDetermined = sessionStorage.getItem(ADMIN_STATUS_KEY) === 'true';
    const lastKnownAdminStatus = sessionStorage.getItem(ADMIN_VALUE_KEY) === 'true';
    
    // If we're impersonating, always show
    if (isAdminImpersonating) {
      if (!hasEverDetermined) {
        console.log('[Sidebar] Setting admin status determined (impersonating)');
        sessionStorage.setItem(ADMIN_STATUS_KEY, 'true');
        sessionStorage.setItem(ADMIN_VALUE_KEY, 'true');
      }
      return true;
    }
    
    // If we have role data, make determination and persist both the fact we determined it AND the result
    if (hasRolesData) {
      if (!hasEverDetermined) {
        console.log('[Sidebar] Setting admin status determined (has roles data)');
        sessionStorage.setItem(ADMIN_STATUS_KEY, 'true');
      }
      // Always update the actual admin status when we have fresh role data
      sessionStorage.setItem(ADMIN_VALUE_KEY, hasAdminRole.toString());
      return hasAdminRole;
    }
    
    // If we haven't determined status yet, don't show anything (prevents flash)
    if (!hasEverDetermined) {
      return false;
    }
    
    // If we've determined status before but are temporarily without role data, 
    // use the last known admin status to prevent flashing
    return lastKnownAdminStatus;
  }, [userRoles]); // Only depend on userRoles



  // NEW: Get all routes - use dynamic routes from database if available, fallback to hardcoded
  const allSidebarRoutes = useMemo(() => {
    // If feature flags are loaded, build routes from database metadata
    if (featureFlags && Object.keys(featureFlags).length > 0) {
      return buildRoutesFromFeatureFlags(featureFlags);
    }
    // Fallback to hardcoded routes while feature flags load or if API fails
    return getSortedRoutes(true); // true = sidebar-visible routes only
  }, [featureFlags]); // Rebuild when feature flags change

  // Define the special routes that should always appear at the end (by order number)
  const endRoutes = ['/faq', '/settings', '/admin'];
  
  // NEW: Create stable route access map from feature flags (replaces role checking)
  // Enhanced to include denial reasons for proper UI messaging
  const routeAccessMap = useMemo(() => {
    const accessMap = new Map<string, { state: 'loading' | 'granted' | 'denied', reason?: string }>();
    
    // Still loading feature flags or roles
    if (!hasEverLoaded && (rolesLoading || flagsLoading)) {
      allSidebarRoutes.forEach(route => {
        accessMap.set(route.path, { state: 'loading' });
      });
      return accessMap;
    }
    
    // No feature flags loaded yet - use loading state
    if (!featureFlags) {
      allSidebarRoutes.forEach(route => {
        accessMap.set(route.path, { state: 'loading' });
      });
      return accessMap;
    }
    
    // Map feature flag access to routes
    allSidebarRoutes.forEach(route => {
      const flag = featureFlags[route.featureKey];
      
      if (!flag) {
        // Feature flag not found - deny access for safety
        console.warn(`[Sidebar] No feature flag found for route: ${route.path} (key: ${route.featureKey})`);
        accessMap.set(route.path, { state: 'denied', reason: 'Feature not found' });
        return;
      }
      
      // Use feature flag enabled status and reason
      if (flag.enabled) {
        accessMap.set(route.path, { state: 'granted' });
      } else {
        accessMap.set(route.path, { state: 'denied', reason: flag.reason });
      }
    });
    
    return accessMap;
  }, [allSidebarRoutes, featureFlags, hasEverLoaded, rolesLoading, flagsLoading]);

  // Separate routes into accessible, locked, and end routes - memoized for performance
  const { accessibleRoutes, lockedRoutes, finalRoutes } = useMemo(() => {
    const accessible = allSidebarRoutes.filter(route => {
      const access = routeAccessMap.get(route.path);
      return !endRoutes.includes(route.path) && access?.state === 'granted';
    });
    
    const locked = allSidebarRoutes.filter(route => {
      const access = routeAccessMap.get(route.path);
      return !endRoutes.includes(route.path) && (access?.state === 'denied' || access?.state === 'loading');
    });
    
    const final = allSidebarRoutes.filter(route => 
      endRoutes.includes(route.path)
    );

    // Sort final routes in the specified order: faq, settings, admin
    final.sort((a, b) => {
      const aIndex = endRoutes.indexOf(a.path);
      const bIndex = endRoutes.indexOf(b.path);
      return aIndex - bIndex;
    });

    return { accessibleRoutes: accessible, lockedRoutes: locked, finalRoutes: final };
  }, [allSidebarRoutes, routeAccessMap, endRoutes]); // Use stable dependencies

  // Combine all routes with stable memoization to prevent unnecessary re-renders
  const sidebarNavLinks = useMemo(() => {
    return [...accessibleRoutes, ...lockedRoutes, ...finalRoutes];
  }, [accessibleRoutes, lockedRoutes, finalRoutes]);

  // Optimized link variants - disable animation after first render and once roles are determined
  const linkVariants = useMemo(() => ({
    hidden: { opacity: hasAnimated || hasEverLoaded ? 1 : 0, x: hasAnimated || hasEverLoaded ? 0 : -20 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: hasAnimated || hasEverLoaded ? { duration: 0 } : {
        delay: i * 0.05 + 0.2,
        duration: 0.3,
      },
    }),
  }), [hasAnimated, hasEverLoaded]);

  return (
    <>
      {/* Overlay for mobile */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden cursor-pointer" 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleSidebar}
            aria-hidden="true" // Hide from screen readers
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`
          bg-white/10 backdrop-blur-xl border-r border-white/20 text-white w-60 pt-2 px-3 pb-4 flex flex-col flex-shrink-0 h-screen
          fixed inset-y-0 left-0 z-50 shadow-2xl
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:sticky md:top-0 md:shadow-xl md:border-r md:border-white/20
          md:translate-x-0 md:transition-none md:z-auto
          ${hasAnimated ? 'layout-persistent' : ''}
        `} 
        style={{ touchAction: 'pan-y' }}
      >
        {/* Mobile Close Button */}
        <motion.button
          onClick={toggleSidebar}
          className="absolute top-1 right-1 text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 md:hidden cursor-pointer z-10 backdrop-blur-sm border border-white/20"
          aria-label="Close sidebar"
          whileHover={{ scale: 1.1, rotate: 90 }}
          whileTap={{ scale: 0.9 }}
        >
          <XMarkIcon className="w-4 h-4" />
        </motion.button>

        {/* Logo/Header Area */}
        <motion.div 
          className="flex items-center h-16 px-0 pt-2 pb-4 border-b border-white/10 space-x-4 mb-4 mt-0"
          initial={hasAnimated ? false : { opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: hasAnimated ? 0 : 0.5 }}
        >
           <motion.img 
             src={logo} 
             alt="Bot Blender Logo" 
             className="w-[50px] h-[50px] flex-shrink-0 rounded-xl shadow-lg" 
             whileHover={{ scale: 1.05, rotate: 2 }}
             transition={{ duration: 0.3, ease: "easeOut" }}
             initial={hasAnimated ? false : { scale: 0.8, opacity: 0 }}
             animate={{ scale: 1, opacity: 1 }}
           />
           <motion.span 
             className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text"
             initial={hasAnimated ? false : { opacity: 0 }}
             animate={{ opacity: 1 }}
             transition={{ delay: hasAnimated ? 0 : 0.2, duration: hasAnimated ? 0 : 0.5 }}
           >
             Bot Blender
           </motion.span>
        </motion.div>

        {/* Navigation */}
        <nav 
          className="flex-grow space-y-2" 
          style={{ touchAction: 'manipulation' }}
        >
          {sidebarNavLinks.map((link, index) => {
            const Icon = link.icon || ChartPieIcon; // Use icon from route config
            const accessInfo = routeAccessMap.get(link.path) || { state: 'granted' };
            const accessState = accessInfo.state;
            const denialReason = accessInfo.reason;
            
            // Special handling for admin route visibility
            const isAdminRoute = link.path === '/admin';
            const shouldHideAdmin = isAdminRoute && !isAdminVisible;
            
            // Determine visual state based on access
            const isLoading = accessState === 'loading';
            const isDisabled = accessState === 'denied';
            
            // Determine the type of denial for appropriate messaging
            const isGloballyDisabled = denialReason?.includes('globally disabled');

            return (
              <motion.div // Wrap NavLink in motion.div for variants
                key={link.path}
                custom={index}
                variants={linkVariants}
                initial="hidden"
                animate="visible"
                style={{
                  display: shouldHideAdmin ? 'none' : 'block'
                }}
              >
                <NavLink
                  to={link.path}
                  onClick={() => {
                    // Close sidebar on mobile after navigation
                    if (window.innerWidth < 768) {
                      toggleSidebar();
                    }
                    // Let React Router handle navigation naturally - no page refresh
                  }}
                  onTouchStart={() => {
                    // Improve touch responsiveness on iOS Safari
                  }}
                  className={({ isActive }) => {
                    if (isLoading) {
                      return 'flex items-center space-x-3 px-4 py-3 rounded-xl text-sm transition-all duration-300 ease-out opacity-60';
                    }
                    return `flex items-center space-x-3 px-4 py-3 rounded-xl text-sm transition-all duration-300 ease-out group relative overflow-hidden cursor-pointer touch-manipulation ${
                      isActive && !isDisabled
                        ? 'bg-gradient-to-r from-slate-500/15 to-slate-400/10 text-white font-semibold shadow-lg border-l-4 border-emerald-400'
                        : isDisabled
                        ? 'text-white/40 font-medium cursor-not-allowed opacity-50'
                        : 'text-white/70 font-medium hover:bg-white/5 hover:text-white hover:translate-x-1 hover:shadow-md'
                    }`;
                  }}
                  aria-disabled={isDisabled || isLoading}
                  style={{ touchAction: 'manipulation' }}
                >
                  {({ isActive }) => {
                    // Render skeleton loader during loading state
                    if (isLoading) {
                      return (
                        <>
                          <div className="w-5 h-5 bg-white/20 rounded animate-pulse flex-shrink-0" />
                          <div className="flex flex-col flex-1">
                            <div className="h-4 bg-white/20 rounded animate-pulse w-20" />
                          </div>
                        </>
                      );
                    }
                    
                    // Normal rendering
                    return (
                      <>
                        {/* Active background glow */}
                        {isActive && !isDisabled && (
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-emerald-500/8 to-slate-500/5 rounded-xl"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          />
                        )}
                        
                        <Icon className={`w-5 h-5 flex-shrink-0 transition-all duration-300 ease-out relative z-10 ${
                          isActive && !isDisabled ? 'text-emerald-300 scale-110' :
                          isDisabled ? 'text-white/30' :
                          'text-white/60 group-hover:text-slate-200 group-hover:scale-105'
                        }`} />
                        <div className="flex flex-col relative z-10">
                          <span className={`nav-label transition-all duration-300 ${isDisabled ? 'italic' : ''} ${
                            isActive && !isDisabled ? 'text-white' : 'group-hover:text-white'
                          }`}>
                            {link.label}
                          </span>
                          {isDisabled && (
                            <motion.span 
                              className={`text-xs mt-0.5 flex items-center ${
                                isGloballyDisabled ? 'text-gray-400' : 'text-amber-400'
                              }`}
                              initial={{ opacity: 0, y: -5 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.1 }}
                            >
                               {isGloballyDisabled ? 'Temporarily Unavailable' : 'Upgrade Required'}
                               <LockClosedIcon className={`w-3 h-3 inline ml-1 ${
                                 isGloballyDisabled ? 'text-gray-400' : 'text-amber-400'
                               }`} />
                            </motion.span>
                          )}
                        </div>
                      </>
                    );
                  }}
                </NavLink>
              </motion.div>
            );
          })}
        </nav>

        {/* Optional Footer Area in Sidebar */}
        <motion.div 
          className="mt-auto pt-4 border-t border-white/10"
          initial={hasAnimated ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: hasAnimated ? 0 : 0.5, duration: hasAnimated ? 0 : 0.5 }}
        >
            <p className="text-xs text-white/40 text-center font-light tracking-wide">© {new Date().getFullYear()} Bot Blender</p>
        </motion.div>
      </aside>
    </>
  );
};

export default React.memo(Sidebar);